import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, TextField, MenuItem, Button,
  Card, CardContent, Checkbox, FormControlLabel, Alert,
  CircularProgress, Chip, Divider, Snackbar, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { CheckCircle, Login as LoginIcon, DoneAll, WarningAmber } from '@mui/icons-material';
import { format } from 'date-fns';

const API = '/api/nursing';

const SESSION_LABEL = { am: 'AM', pm: 'PM', all_day: 'All Day' };

const DELAY_OPTIONS = [
  { value: '',         label: 'No delay' },
  { value: '0-30',    label: '0–30 mins' },
  { value: '30-60',   label: '30–60 mins' },
  { value: '60-90',   label: '60–90 mins' },
  { value: 'over-90', label: 'Over 90 mins' },
];

const DELAY_REASONS = [
  'Clinician arrived late',
  'Clinic overbooked',
  'Inappropriate appointment length',
  'Patient became unwell / Clinic emergency',
  'Other',
];

// ── PIN screen ───────────────────────────────────────────────────────────────

function PinScreen({ onSuccess }) {
  const [pin, setPin]       = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/verify-pin`, { pin });
      // Store auth in sessionStorage so it persists across page refreshes
      // but clears when the browser tab is closed
      sessionStorage.setItem('nursing_auth', Date.now().toString());
      onSuccess();
    } catch {
      setError('Incorrect PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', bgcolor: '#f5f5f5',
    }}>
      <Card sx={{ maxWidth: 360, width: '100%', p: 2 }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/nhs-logo.png"
              alt="NHS"
              sx={{ height: 56, mb: 2 }}
            />
            <Typography variant="h5" fontWeight="bold">Nursing Checks</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cambridge University Hospitals
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              label="Access PIN"
              type="password"
              fullWidth
              value={pin}
              onChange={e => setPin(e.target.value)}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ mb: 2 }}
              autoFocus
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={pin.length !== 6 || loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
            >
              {loading ? 'Checking…' : 'Enter'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Booking check card ────────────────────────────────────────────────────────

function BookingCheckCard({ booking, specialties, onSaved }) {
  const [form, setForm] = useState({
    room_status:                   booking.room_status || 'used_as_planned',
    used_by_specialty:             booking.used_by_specialty || '',
    clinic_delay:                  booking.clinic_delay || '',
    delay_reason:                  booking.delay_reason || '',
    delay_impacted_another_clinic: booking.delay_impacted_another_clinic || false,
    additional_issues:             booking.additional_issues || '',
  });
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(!!booking.check_id);
  const [error,   setError]   = useState('');

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/checks`, { booking_id: booking.id, ...form,
        used_by_specialty: form.room_status === 'reassigned' ? form.used_by_specialty : null,
      });
      setSaved(true);
      onSaved();
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const sessionColor = booking.color || '#B082E4';

  return (
    <Card sx={{ mb: 2, borderLeft: `5px solid ${sessionColor}` }}>
      <CardContent>
        {/* Booking header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {booking.room_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {SESSION_LABEL[booking.session] || booking.session}
              {booking.specialty ? ` · ${booking.specialty}` : ''}
              {booking.doctor_name ? ` · ${booking.doctor_name}` : ''}
            </Typography>
            {booking.clinic_code && (
              <Typography variant="caption" color="text.secondary">
                Code: {booking.clinic_code}
              </Typography>
            )}
          </Box>
          {saved && (
            <Chip icon={<CheckCircle />} label="Saved" color="success" size="small" />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Room status — 3 horizontal options */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Room status
        </Typography>
        <ToggleButtonGroup
          value={form.room_status}
          exclusive
          fullWidth
          size="small"
          onChange={(_, val) => {
            if (!val) return;
            setForm(prev => ({
              ...prev,
              room_status: val,
              used_by_specialty: val !== 'reassigned' ? '' : prev.used_by_specialty,
              ...(val === 'vacant' ? { clinic_delay: '', delay_reason: '', delay_impacted_another_clinic: false } : {}),
            }));
            setSaved(false);
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="used_as_planned" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
            Room used<br />as planned
          </ToggleButton>
          <ToggleButton value="reassigned" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
            Room<br />reassigned
          </ToggleButton>
          <ToggleButton value="vacant" sx={{ fontSize: '0.75rem', lineHeight: 1.2, color: 'error.main' }}>
            Room left<br />vacant
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Reassigned to — only shown if reassigned */}
        {form.room_status === 'reassigned' && (
          <TextField
            select
            label="Room reassigned to"
            fullWidth
            size="small"
            value={form.used_by_specialty}
            onChange={e => set('used_by_specialty', e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">— Select specialty —</MenuItem>
            {specialties.map(s => (
              <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
            ))}
          </TextField>
        )}

        {/* Clinic delay */}
        <TextField
          select
          label="Clinic delay"
          fullWidth
          size="small"
          value={form.clinic_delay}
          disabled={form.room_status === 'vacant'}
          onChange={e => {
            set('clinic_delay', e.target.value);
            if (!e.target.value) set('delay_reason', '');
          }}
          sx={{ mb: 2 }}
        >
          {DELAY_OPTIONS.map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>

        {/* Delay reason */}
        <TextField
          select
          label="Delay reason"
          fullWidth
          size="small"
          value={form.delay_reason}
          onChange={e => set('delay_reason', e.target.value)}
          disabled={form.room_status === 'vacant' || !form.clinic_delay}
          sx={{ mb: 2 }}
        >
          <MenuItem value="">— Select reason —</MenuItem>
          {DELAY_REASONS.map(r => (
            <MenuItem key={r} value={r}>{r}</MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Checkbox
              checked={form.delay_impacted_another_clinic}
              onChange={e => set('delay_impacted_another_clinic', e.target.checked)}
              color="warning"
              disabled={form.room_status === 'vacant' || !form.clinic_delay}
            />
          }
          label="Did the delay impact another clinic?"
          sx={{ mb: 2, display: 'block' }}
        />

        {/* Additional issues */}
        <TextField
          label="Any additional issues to raise"
          fullWidth
          multiline
          rows={2}
          size="small"
          value={form.additional_issues}
          onChange={e => set('additional_issues', e.target.value)}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          color={saved ? 'success' : 'primary'}
          fullWidth
        >
          {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Check'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NursingChecks() {
  const [authed,     setAuthed]     = useState(() => !!sessionStorage.getItem('nursing_auth'));
  const [date,       setDate]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clinicId,   setClinicId]   = useState('');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [clinics,    setClinics]    = useState([]);
  const [bookings,   setBookings]   = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState('');
  const [saveAllOpen,   setSaveAllOpen]   = useState(false);
  const [saveAllBusy,   setSaveAllBusy]   = useState(false);

  useEffect(() => {
    if (!authed) return;
    axios.get('/api/public/clinics').then(r => {
      const sorted = [...r.data].sort((a, b) => {
        const numA = parseInt((a.clinic_name.match(/\d+/) || [0])[0]);
        const numB = parseInt((b.clinic_name.match(/\d+/) || [0])[0]);
        if (numA !== numB) return numA - numB;
        return a.clinic_name.localeCompare(b.clinic_name);
      });
      setClinics(sorted);
    }).catch(() => {});
    axios.get('/api/public/specialties').then(r => setSpecialties(r.data)).catch(() => {});
  }, [authed]);

  const loadBookings = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const params = { date };
      if (clinicId) params.clinic_id = clinicId;
      const { data } = await axios.get(`${API}/bookings`, { params });
      setBookings(data);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [date, clinicId]);

  useEffect(() => {
    if (authed) loadBookings();
  }, [authed, loadBookings]);

  const handleSaveAll = async () => {
    const unsaved = filteredBookings.filter(b => !b.check_id);
    if (!unsaved.length) {
      setSaveAllOpen(false);
      return;
    }
    setSaveAllBusy(true);
    try {
      await axios.post(`${API}/checks/bulk`, { booking_ids: unsaved.map(b => b.id) });
      setSaveAllOpen(false);
      setToast(`${unsaved.length} room${unsaved.length !== 1 ? 's' : ''} saved as used as planned.`);
      loadBookings();
    } catch {
      setToast('Failed to save. Please try again.');
    } finally {
      setSaveAllBusy(false);
    }
  };

  if (!authed) return <PinScreen onSuccess={() => setAuthed(true)} />;

  const filteredBookings = sessionFilter === 'all'
    ? bookings
    : bookings.filter(b => b.session === sessionFilter);

  const savedCount = filteredBookings.filter(b => b.check_id).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#005EB8', color: 'white', p: 2, mb: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box component="img" src="/nhs-logo.png" alt="NHS" sx={{ height: 40 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Nursing Checks</Typography>
              <Typography variant="caption">Cambridge University Hospitals — Room Utilisation</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md">
        {/* Date + Clinic selector */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                value={date}
                onChange={e => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                select
                label="Clinic"
                size="small"
                value={clinicId}
                onChange={e => setClinicId(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All Clinics</MenuItem>
                {clinics.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.clinic_name}</MenuItem>
                ))}
              </TextField>
              <ToggleButtonGroup
                value={sessionFilter}
                exclusive
                size="small"
                onChange={(_, val) => { if (val) setSessionFilter(val); }}
              >
                <ToggleButton value="all" sx={{ '&.Mui-selected': { bgcolor: '#757575', color: 'white', '&:hover': { bgcolor: '#616161' } } }}>All</ToggleButton>
                <ToggleButton value="am"  sx={{ '&.Mui-selected': { bgcolor: '#B082E4', color: 'white', '&:hover': { bgcolor: '#8B5CC8' } } }}>AM</ToggleButton>
                <ToggleButton value="pm"  sx={{ '&.Mui-selected': { bgcolor: '#e65100', color: 'white', '&:hover': { bgcolor: '#bf360c' } } }}>PM</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" onClick={loadBookings} disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
              </Button>
              {bookings.length > 0 && (
                <Chip
                  label={`${savedCount} / ${filteredBookings.length} completed`}
                  color={savedCount === filteredBookings.length ? 'success' : 'default'}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Save All button — shown when there are unsaved bookings */}
        {!loading && filteredBookings.length > 0 && filteredBookings.some(b => !b.check_id) && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="error"
              fullWidth
              size="large"
              startIcon={<DoneAll />}
              onClick={() => setSaveAllOpen(true)}
              sx={{ py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}
            >
              Save All as Used as Planned ({filteredBookings.filter(b => !b.check_id).length} unsaved)
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
              Only use this option if all rooms in the period selected above were used as planned with no delays or issues.
            </Typography>
          </Box>
        )}

        {/* Booking cards */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredBookings.length === 0 ? (
          <Alert severity="info">
            No confirmed bookings found for {format(new Date(date + 'T12:00:00'), 'EEEE d MMMM yyyy')}
            {clinicId ? ' in the selected clinic' : ''}
            {sessionFilter !== 'all' ? ` in the ${sessionFilter.toUpperCase()} session` : ''}.
          </Alert>
        ) : (
          filteredBookings.map(booking => (
            <BookingCheckCard
              key={booking.id}
              booking={booking}
              specialties={specialties}
              onSaved={loadBookings}
            />
          ))
        )}
      </Container>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />

      <Dialog open={saveAllOpen} onClose={() => { if (!saveAllBusy) setSaveAllOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="error" />
          Save All as Used as Planned
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will mark all <strong>{filteredBookings.filter(b => !b.check_id).length} unsaved room{filteredBookings.filter(b => !b.check_id).length !== 1 ? 's' : ''}</strong> as <em>used as planned</em> with no delays or issues recorded.
          </Typography>
          <Alert severity="warning" icon={false}>
            Please only use this option if <strong>all rooms</strong> in the period you have selected above were used as planned, with no delays, no vacant rooms, and no rooms that were reassigned.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveAllOpen(false)} disabled={saveAllBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAll}
            variant="contained"
            color="error"
            disabled={saveAllBusy}
            startIcon={saveAllBusy ? <CircularProgress size={16} color="inherit" /> : <DoneAll />}
          >
            {saveAllBusy ? 'Saving…' : 'Yes, save all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
